class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);

/*
 * DinoDarts navigation reliability fix
 *
 * The theme currently has two independent click handlers competing for nested
 * menu <summary> elements in the mobile drawer. One toggles the native <details>
 * state and height animation while MenuDrawer adds its own delayed menu-opening
 * state. On quick or repeated interactions this can leave the submenu in an
 * inconsistent state and makes it feel laggy.
 *
 * Handle only nested drawer accordions here, in capture phase, so there is one
 * deterministic owner for the interaction. Top-level drawer and desktop mega
 * menu behaviour remain untouched.
 */
function initDinoNestedMenuFix() {
  document.querySelectorAll('header-drawer').forEach((drawer) => {
    if (drawer.dataset.dinoNestedMenuFix === 'true') return;
    drawer.dataset.dinoNestedMenuFix = 'true';

    drawer.addEventListener(
      'click',
      (event) => {
        const summary = event.target.closest('.menu-list-closest details > summary');
        if (!summary || !drawer.contains(summary)) return;

        const details = summary.parentElement;
        const submenu = summary.nextElementSibling;
        if (!details || !submenu?.classList.contains('menu-drawer__submenu')) return;

        event.preventDefault();
        event.stopPropagation();

        const duration = 190;
        const isOpen = details.hasAttribute('open');

        if (submenu._dinoMenuTimeout) {
          window.clearTimeout(submenu._dinoMenuTimeout);
          submenu._dinoMenuTimeout = null;
        }

        const finishAfterTransition = (callback) => {
          let finished = false;
          const done = () => {
            if (finished) return;
            finished = true;
            submenu.removeEventListener('transitionend', onEnd);
            if (submenu._dinoMenuTimeout) {
              window.clearTimeout(submenu._dinoMenuTimeout);
              submenu._dinoMenuTimeout = null;
            }
            callback();
          };
          const onEnd = (transitionEvent) => {
            if (transitionEvent.target === submenu && transitionEvent.propertyName === 'height') done();
          };

          submenu.addEventListener('transitionend', onEnd);
          submenu._dinoMenuTimeout = window.setTimeout(done, duration + 100);
        };

        submenu.style.transition = `height ${duration}ms ease, opacity 160ms ease`;
        submenu.style.overflow = 'hidden';

        if (isOpen) {
          const currentHeight = submenu.getBoundingClientRect().height || submenu.scrollHeight;
          submenu.style.height = `${currentHeight}px`;
          submenu.style.opacity = '1';
          submenu.offsetHeight;

          summary.setAttribute('aria-expanded', 'false');
          submenu.style.height = '0px';
          submenu.style.opacity = '0';

          finishAfterTransition(() => {
            details.classList.remove('menu-opening');
            details.removeAttribute('open');
            submenu.style.height = '';
            submenu.style.opacity = '';
            submenu.style.overflow = '';
            submenu.style.transition = '';
          });
        } else {
          details.setAttribute('open', '');
          details.classList.add('menu-opening');
          summary.setAttribute('aria-expanded', 'true');

          submenu.style.height = '0px';
          submenu.style.opacity = '0';
          submenu.offsetHeight;
          submenu.style.height = `${submenu.scrollHeight}px`;
          submenu.style.opacity = '1';

          finishAfterTransition(() => {
            submenu.style.height = 'auto';
            submenu.style.opacity = '';
            submenu.style.overflow = '';
            submenu.style.transition = '';
          });
        }
      },
      true
    );
  });
}

initDinoNestedMenuFix();
document.addEventListener('shopify:section:load', initDinoNestedMenuFix);

/*
 * DinoDarts menu product DOM deferral
 * Keep hidden collection product cards out of the live header DOM until needed.
 */
function initDinoMenuProductDeferral(root = document) {
  const MAX_MENU_PRODUCTS = 8;
  const desktopPanels = root.querySelectorAll('.menu-product-list-desk .grandchildlink-products');
  const mobilePanels = root.querySelectorAll('.menu-prdocut-list-mobile .grandchildlink-products');

  const setupPanel = (panel, keepCount) => {
    if (panel.dataset.dinoProductDeferral === 'true') return;

    const allCards = Array.from(panel.children).filter((child) => child.classList?.contains('custom-product-list'));
    if (!allCards.length) {
      panel.dataset.dinoProductDeferral = 'true';
      return;
    }

    // Hard cap every menu collection at eight bestseller cards.
    allCards.slice(MAX_MENU_PRODUCTS).forEach((card) => card.remove());
    const cards = allCards.slice(0, MAX_MENU_PRODUCTS);
    const safeKeepCount = Math.min(keepCount, MAX_MENU_PRODUCTS);

    const fragment = document.createDocumentFragment();
    cards.slice(safeKeepCount).forEach((card) => fragment.appendChild(card));

    panel._dinoDeferredProductCards = fragment;
    panel._dinoVisibleProductCards = Math.min(safeKeepCount, cards.length);
    panel.dataset.dinoProductDeferral = 'true';
  };

  const restorePreview = (panel, targetCount = 5) => {
    const fragment = panel?._dinoDeferredProductCards;
    if (!panel || !fragment || !fragment.childNodes.length) return;

    const safeTargetCount = Math.min(targetCount, MAX_MENU_PRODUCTS);
    while (panel._dinoVisibleProductCards < safeTargetCount && fragment.firstChild) {
      panel.appendChild(fragment.firstChild);
      panel._dinoVisibleProductCards += 1;
    }
  };

  const restoreAll = (panel) => {
    const fragment = panel?._dinoDeferredProductCards;
    if (!panel || !fragment || !fragment.childNodes.length) return;

    panel.appendChild(fragment);
    panel._dinoVisibleProductCards = Math.min(
      panel.querySelectorAll(':scope > .custom-product-list').length,
      MAX_MENU_PRODUCTS
    );
  };

  const scheduleRestoreAll = (panel) => {
    if (!panel || panel.dataset.dinoRestoreScheduled === 'true') return;
    panel.dataset.dinoRestoreScheduled = 'true';

    const restore = () => {
      panel.dataset.dinoRestoreScheduled = 'false';
      restoreAll(panel);
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(restore, { timeout: 700 });
    } else {
      window.setTimeout(restore, 220);
    }
  };

  desktopPanels.forEach((panel) => {
    setupPanel(panel, panel.classList.contains('active') ? 5 : 0);

    panel.addEventListener('mouseenter', () => {
      restorePreview(panel, 5);
      scheduleRestoreAll(panel);
    }, { passive: true });

    panel.addEventListener('wheel', () => restoreAll(panel), { passive: true, once: true });
    panel.addEventListener('focusin', () => restoreAll(panel), { once: true });
  });

  mobilePanels.forEach((panel) => setupPanel(panel, 0));

  if (!document.documentElement.dataset.dinoDesktopMenuHydration) {
    document.documentElement.dataset.dinoDesktopMenuHydration = 'true';

    document.addEventListener(
      'mouseover',
      (event) => {
        const menuItem = event.target.closest('.menu_hover');
        if (!menuItem || menuItem.contains(event.relatedTarget)) return;

        const megaWrapper = menuItem.closest('.mega_main_wrp');
        const title = menuItem.dataset.title;
        if (!megaWrapper || !title) return;

        const panel = Array.from(
          megaWrapper.querySelectorAll('.menu-product-list-desk .grandchildlink-products')
        ).find((candidate) => candidate.dataset.content === title);

        if (panel) restorePreview(panel, 5);
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        const summary = event.target.closest('.menu-prdocut-list-mobile > details > summary');
        if (!summary) return;

        const details = summary.parentElement;
        details.querySelectorAll('.grandchildlink-products.active').forEach((panel) => {
          restorePreview(panel, 5);
          scheduleRestoreAll(panel);
        });
      },
      true
    );
  }
}

initDinoMenuProductDeferral();
document.addEventListener('shopify:section:load', (event) => initDinoMenuProductDeferral(event.target));
